if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // San Rocco Italia: optional engraving and gift wrapping controls
        // are rendered in a separate product-information block.
        this.personalisation = this.closest('product-info')?.querySelector('.sr-personalisation') || null;
        this.setupPersonalisation();
      }

      setupPersonalisation() {
        if (!this.personalisation) return;

        const root = this.personalisation;
        this.personalisationFields = {
          giftToggle: root.querySelector('[data-sr-gift-toggle]'),
          giftMessage: root.querySelector('[data-sr-gift-message]') || root.querySelector('[name="properties[Gift message]"]'),
          engravingToggle: root.querySelector('[data-sr-engraving-toggle]'),
          font: root.querySelector('[data-sr-font]') || root.querySelector('[name="properties[Font]"]'),
          engravingText: root.querySelector('[data-sr-engraving-text]') || root.querySelector('[name="properties[Custom text]"]'),
        };

        // Do not submit add-on fields as raw line item properties; the
        // handler below attaches them to the handbag and adds paid services.
        Object.values(this.personalisationFields).forEach((field) => {
          if (!field) return;
          field.removeAttribute('name');
          field.removeAttribute('form');
        });

        [this.personalisationFields.giftToggle, this.personalisationFields.engravingToggle].forEach((toggle) => {
          toggle?.addEventListener('change', () => this.updateDynamicCheckoutVisibility());
        });
        this.updateDynamicCheckoutVisibility();
      }

      getPersonalisationState() {
        if (!this.personalisationFields || !this.personalisation) return null;
        const fields = this.personalisationFields;
        const root = this.personalisation;

        return {
          giftSelected: fields.giftToggle?.checked === true,
          giftMessage: fields.giftMessage?.value?.trim() || '',
          engravingSelected: fields.engravingToggle?.checked === true,
          engravingIsFree: root.dataset.engravingFree === 'true',
          engravingLimit: parseInt(root.dataset.engravingLimit || '0', 10) || 0,
          font: fields.font?.value || '',
          engravingText: fields.engravingText?.value?.trim() || '',
          giftWrapVariantId: Number(root.dataset.giftWrapVariantId || 0),
          engravingVariantId: Number(root.dataset.engravingVariantId || 0),
        };
      }

      updateDynamicCheckoutVisibility() {
        const state = this.getPersonalisationState();
        const hasAddOn = !!(state && (state.giftSelected || state.engravingSelected));
        const button = this.querySelector('.shopify-payment-button') || this.querySelector('[data-shopify="payment-button"]');
        if (button) button.hidden = hasAddOn;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);
        const personalisation = this.getPersonalisationState();
        const hasAddOn = !!(personalisation && (personalisation.giftSelected || personalisation.engravingSelected));

        // Avoid another app's submit handler processing the same personalised purchase.
        if (hasAddOn) evt.stopImmediatePropagation();

        if (!variantId) {
          this.handleErrorMessage('Please select an available product option.');
          return;
        }

        // Stop before adding anything if a required paid service is not linked.
        if (hasAddOn && personalisation.giftSelected && !personalisation.giftWrapVariantId) {
          this.handleErrorMessage('Gift wrapping is currently unavailable. Please contact us.');
          return;
        }
        if (hasAddOn && personalisation.engravingSelected && !personalisation.engravingIsFree && !personalisation.engravingVariantId) {
          this.handleErrorMessage('Paid engraving is currently unavailable. Please contact us.');
          return;
        }
        if (hasAddOn && personalisation.engravingSelected && !personalisation.engravingText) {
          this.handleErrorMessage('Please enter the text you would like engraved.');
          this.personalisationFields.engravingText?.focus();
          return;
        }
        if (hasAddOn && personalisation.engravingSelected && personalisation.engravingLimit > 0 &&
            personalisation.engravingText.length > personalisation.engravingLimit) {
          this.handleErrorMessage(`Engraving must be ${personalisation.engravingLimit} characters or fewer.`);
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const addedLines = [{ id: variantId, quantity }];
        if (hasAddOn) {
          // Keep any ordinary properties added by Shopify or other apps.
          const properties = {};
          for (const [key, value] of formData.entries()) {
            if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
            const propertyName = key.slice(11, -1);
            // Personalisation is written below using short, consistent labels.
            // Ignore legacy form inputs so they cannot reintroduce long labels
            // such as "Engraving?" or "Custom text (30 characters max...)".
            const label = propertyName.trim().toLowerCase();
            const personalisationLabels = [
              'gift wrap', 'gift message', 'engraving', 'engraving?', 'font', 'custom text',
            ];
            if (personalisationLabels.includes(label) || label.startsWith('custom text (')) continue;
            if (propertyName) properties[propertyName] = value;
          }

          if (personalisation.giftSelected) {
            properties['Gift wrap'] = 'Yes';
            if (personalisation.giftMessage) properties['Gift message'] = personalisation.giftMessage;
          }
          if (personalisation.engravingSelected) {
            properties['Engraving'] = 'Yes';
            if (personalisation.font) properties['Font'] = personalisation.font;
            properties['Custom text'] = personalisation.engravingText;
          }

          const parentId = Number(variantId);
          const mainItem = { id: parentId, quantity, properties };
          const sellingPlan = formData.get('selling_plan');
          if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

          const items = [mainItem];
          if (personalisation.giftSelected) {
            items.push({ id: personalisation.giftWrapVariantId, quantity, parent_id: parentId });
          }
          if (personalisation.engravingSelected && !personalisation.engravingIsFree) {
            items.push({ id: personalisation.engravingVariantId, quantity, parent_id: parentId });
          }

          const payload = { items };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
          addedLines.push(...items.slice(1));
        } else {
          // Preserve the native Studio 16.1.0 request for ordinary purchases.
          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity, addedLines);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage && !hasAddOn) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              } else {
                // An add-on error should not permanently lock Add to Cart.
                this.error = false;
              }
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            // Multi-item /cart/add.js responses wrap the added lines in `items`.
            // Studio's cart notification expects `key` on the response itself.
            const mainAddedLine = Array.isArray(response.items)
              ? response.items.find((item) => String(item.id) === String(variantId)) || response.items[0]
              : null;
            const renderedResponse = mainAddedLine
              ? { ...mainAddedLine, sections: response.sections }
              : response;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(renderedResponse);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(renderedResponse);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity, addedLines = [{ id: variantId, quantity }]) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: addedLines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
